import React from 'react';
import {
  Activity,
  CheckCircle2,
  MapPin,
  FlaskConical,
  FileText,
  PhoneCall,
} from 'lucide-react';
import type { ActivityItem } from './types';

interface ActivityTimelineProps {
  activities: ActivityItem[];
}

export const ActivityTimeline: React.FC<ActivityTimelineProps> = ({ activities }) => {
  const getIcon = (type: string) => {
    switch (type) {
      case 'visit':
        return <MapPin className="h-3.5 w-3.5" />;
      case 'sample':
        return <FlaskConical className="h-3.5 w-3.5" />;
      case 'quotation':
        return <FileText className="h-3.5 w-3.5" />;
      case 'call':
        return <PhoneCall className="h-3.5 w-3.5" />;
      default:
        return <Activity className="h-3.5 w-3.5" />;
    }
  };

  const getStatusBullet = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-success ring-4 ring-success/20 text-on-primary';
      case 'in_progress':
        return 'bg-primary ring-4 ring-primary/20 text-on-primary animate-pulse';
      case 'overdue':
        return 'bg-danger ring-4 ring-danger/20 text-on-primary';
      default:
        return 'bg-slate-300 text-on-primary';
    }
  };

  return (
    <div className="flex h-full flex-col justify-between rounded-card border border-border bg-card p-5 shadow-xs">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between border-b border-outline/80 pb-3">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-container text-primary">
              <Activity className="h-4 w-4" />
            </div>
            <div>
              <span className="text-body-sm font-semibold uppercase tracking-wider text-on-surface">
                Today's Activity
              </span>
              <div className="text-data-xs text-on-surface-subtle">Chronological sales & field log</div>
            </div>
          </div>

          <div className="inline-flex items-center gap-1 rounded-md bg-surface-container px-2 py-0.5 text-data-xs font-semibold text-on-surface-variant">
            <span>Live Timeline</span>
          </div>
        </div>

        {/* Timeline Items */}
        {activities.length === 0 ? (
          <div className="my-6 text-center text-body-sm text-on-surface-subtle">No activity logged yet today.</div>
        ) : (
        <div className="relative my-4 ml-2 border-l border-border pl-4 space-y-4">
          {activities.map((act) => (
            <div key={act.id} className="relative group">
              {/* Node bullet */}
              <div
                className={`absolute -left-[23px] top-0.5 flex h-4 w-4 items-center justify-center rounded-full text-data-xs ${getStatusBullet(
                  act.status
                )}`}
              >
                {act.status === 'completed' && <CheckCircle2 className="h-2.5 w-2.5" />}
              </div>

              {/* Content */}
              <div>
                <div className="flex items-center gap-2 text-body-sm">
                  <span className="font-semibold text-primary">{act.time}</span>
                  <span className="text-on-surface-subtle">•</span>
                  {getIcon(act.type)}
                  <span className="font-semibold text-on-surface">{act.title}</span>
                </div>

                <div className="mt-0.5 text-data-xs font-medium text-on-surface-variant">
                  {act.client}
                </div>

                {act.description && (
                  <p className="mt-1 text-data-xs text-on-surface-subtle leading-snug">
                    {act.description}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
        )}
      </div>

      {/* Footer */}
      <div className="rounded-lg border border-border bg-surface-variant/70 p-3 flex items-center justify-between text-body-sm">
        <span className="text-on-surface-subtle">Daily Progress:</span>
        <span className="font-semibold text-success-text">
          {activities.filter((a) => a.status === 'completed').length} / {activities.length} completed
        </span>
      </div>
    </div>
  );
};
